import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	Spinner,
	addToast
} from '@heroui/react';
import { Copy } from 'lucide-react';
import {
	getCalendarFeedUrl,
	rotateCalendarFeedUrl,
	unsubscribeCalendarFeed
} from '../../../api/userApi.js';
import { refreshCalendarFeedStatus } from '../bootstrapSlice.js';
import { unwrap } from '../utils/apiEnvelope.js';
import { copyToClipboard } from '../utils/copyToClipboard.js';
import { toWebcalUrl } from '../utils/webcalUrl.js';

const LINK_BUTTON_CLASSES =
	'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md no-underline ' +
	'bg-primary text-primary-foreground hover:opacity-90 transition-opacity';

/**
 * SubscribeDialog — the one place a user manages their calendar feed: get
 * the link, put it on another device, replace it, or turn it off. Owns its
 * own URL/rotate/unsubscribe calls; the control only tells it when to be
 * open. Every close (Done, backdrop, Esc) leaves `calendarFeedStatus`
 * current: a fresh open always re-fetches, and a successful rotate or
 * unsubscribe refreshes it inline via `refreshCalendarFeedStatus` so the nav
 * control updates with no reload.
 */
function SubscribeDialog({ isOpen, onClose }) {
	const dispatch = useDispatch();
	const [url, setUrl] = useState('');
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState('');
	const [confirmingRotate, setConfirmingRotate] = useState(false);
	const [rotating, setRotating] = useState(false);
	const [confirmingUnsubscribe, setConfirmingUnsubscribe] = useState(false);
	const [unsubscribing, setUnsubscribing] = useState(false);

	const loadUrl = useCallback(async () => {
		setLoading(true);
		setLoadError('');
		try {
			const payload = unwrap(await getCalendarFeedUrl());
			setUrl(payload?.url || '');
			// Non-blocking, same as handleRotate below: fetching the URL is what
			// ISSUES the token on first call (backend state none -> pending), so
			// Redux needs a refresh here too or the nav control keeps reading
			// "Subscribe to my schedule" until a page reload. A failure refreshing
			// shouldn't undo a successful fetch or surface an error for it.
			dispatch(refreshCalendarFeedStatus());
		} catch (err) {
			setLoadError(err?.message || 'Could not load your calendar feed link.');
		} finally {
			setLoading(false);
		}
	}, [dispatch]);

	useEffect(() => {
		if (isOpen) {
			setConfirmingRotate(false);
			setConfirmingUnsubscribe(false);
			loadUrl();
		}
	}, [isOpen, loadUrl]);

	const handleCopy = async () => {
		try {
			await copyToClipboard(url);
			addToast({
				title: 'Link copied',
				description: 'Paste it wherever your calendar app asks for a URL.',
				color: 'success'
			});
		} catch {
			addToast({
				title: 'Copy failed',
				description: 'Select the link above and copy it manually.',
				color: 'danger'
			});
		}
	};

	const handleRotate = async () => {
		setRotating(true);
		try {
			const payload = unwrap(await rotateCalendarFeedUrl());
			setUrl(payload?.url || '');
			setConfirmingRotate(false);
			// Non-blocking: the nav control re-reads this on its own; a failure
			// here shouldn't stop the rotate from having succeeded.
			dispatch(refreshCalendarFeedStatus());
			addToast({
				title: 'Link rotated',
				description: 'Every device using the old link has stopped updating.',
				color: 'success'
			});
		} catch (err) {
			addToast({
				title: 'Rotate failed',
				description: err?.message || 'Please try again.',
				color: 'danger'
			});
		} finally {
			setRotating(false);
		}
	};

	const handleUnsubscribe = async () => {
		setUnsubscribing(true);
		try {
			unwrap(await unsubscribeCalendarFeed());
			setUrl('');
			setConfirmingUnsubscribe(false);
			// Non-blocking, same as loadUrl/handleRotate above: the nav control
			// re-reads this on its own, and a failure here shouldn't stop the
			// unsubscribe from having succeeded.
			dispatch(refreshCalendarFeedStatus());
			addToast({
				title: 'Feed turned off',
				description:
					"The link has stopped working on every device. Your calendar app keeps showing what it already synced until you remove it there yourself — we can't reach your device to do that for you.",
				color: 'success'
			});
		} catch (err) {
			addToast({
				title: 'Could not turn off the feed',
				description: err?.message || 'Please try again.',
				color: 'danger'
			});
		} finally {
			setUnsubscribing(false);
		}
	};

	const webcalUrl = toWebcalUrl(url);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg" placement="center">
			<ModalContent>
				{close => (
					<>
						<ModalHeader className="flex flex-col gap-1 border-b border-default-200">
							<span className="text-base font-semibold">Subscribe to your schedule</span>
							<span className="text-xs text-default-500 font-normal">
								This link covers your events across all of your organizations — not just the one
								you're viewing now.
							</span>
						</ModalHeader>
						<ModalBody className="py-4 space-y-4">
							{loading ? (
								<div className="flex justify-center py-6">
									<Spinner size="sm" aria-label="Loading your feed link" />
								</div>
							) : loadError ? (
								<div className="space-y-2">
									<p className="text-sm text-danger">{loadError}</p>
									<Button size="sm" variant="bordered" onPress={loadUrl}>
										Try again
									</Button>
								</div>
							) : !url ? (
								<div className="space-y-2">
									<p className="text-sm font-medium">Your calendar feed is off.</p>
									<p className="text-xs text-default-500">
										The link has stopped working on every device. Your calendar app keeps showing
										what it already synced until you remove it there yourself. Subscribe again any
										time to get a new, unrelated link.
									</p>
								</div>
							) : (
								<>
									<div>
										<label className="text-xs font-medium text-default-600 mb-1 block">
											Feed URL — anyone with this link can see your schedule
										</label>
										<div className="flex items-center gap-2">
											<Input
												size="sm"
												value={url}
												isReadOnly
												aria-label="Calendar feed URL"
												classNames={{ input: 'font-mono text-xs' }}
											/>
											<Button
												size="sm"
												variant="bordered"
												isIconOnly
												aria-label="Copy feed URL"
												onPress={handleCopy}
											>
												<Copy className="w-4 h-4" aria-hidden="true" />
											</Button>
										</div>
										<p className="text-xs text-default-500 mt-1">
											This same link works on every device — adding it somewhere new doesn't affect
											the devices already using it, and you don't need a new link to do it.
										</p>
									</div>

									<div className="rounded-md border border-default-200 p-3">
										<p className="text-sm font-medium mb-1">Apple Calendar (iPhone, iPad, Mac)</p>
										<p className="text-xs text-default-600 mb-2">
											Opens your device's own "Subscribe to calendar" screen, where you pick the
											refresh interval yourself.
										</p>
										<a href={webcalUrl} className={LINK_BUTTON_CLASSES}>
											Add to Apple Calendar
										</a>
									</div>

									<div className="rounded-md border border-default-200 p-3">
										<p className="text-sm font-medium mb-1">Google Calendar</p>
										<p className="text-xs text-default-600">
											Google only accepts a feed link from the <strong>desktop web version</strong>{' '}
											of Google Calendar — the Android and iOS apps can't add one. On a computer,
											open Google Calendar, go to <strong>Other calendars → From URL</strong>, and
											paste the link copied above.
										</p>
									</div>

									<p className="text-xs text-default-500">
										Your calendar provider controls how often it refreshes the feed — it can take
										longer than 24 hours to pick up a change.
									</p>
								</>
							)}
						</ModalBody>
						<ModalFooter className="border-t border-default-200">
							{confirmingRotate ? (
								<div className="flex items-center justify-between gap-3 w-full">
									<span className="text-xs text-danger">
										This issues a brand new link and kills the old one immediately. Every device
										already subscribed will stop updating until it's re-added with the new link. You
										don't need this just to add another device — use it when the link may have been
										seen by someone else.
									</span>
									<div className="flex gap-2 shrink-0">
										<Button
											size="sm"
											variant="light"
											onPress={() => setConfirmingRotate(false)}
											isDisabled={rotating}
										>
											Cancel
										</Button>
										<Button size="sm" color="danger" onPress={handleRotate} isLoading={rotating}>
											Rotate anyway
										</Button>
									</div>
								</div>
							) : confirmingUnsubscribe ? (
								<div className="flex items-center justify-between gap-3 w-full">
									<span className="text-xs text-default-700">
										The link stops working everywhere immediately. This calendar stays in your
										calendar app until you remove it there yourself — we can't reach your device to
										do that for you. Subscribing again later gives you a different link; this one
										never comes back.
									</span>
									<div className="flex gap-2 shrink-0">
										<Button
											size="sm"
											variant="light"
											onPress={() => setConfirmingUnsubscribe(false)}
											isDisabled={unsubscribing}
										>
											Cancel
										</Button>
										<Button
											size="sm"
											color="danger"
											onPress={handleUnsubscribe}
											isLoading={unsubscribing}
										>
											Turn off anyway
										</Button>
									</div>
								</div>
							) : (
								<div className="flex items-center justify-between w-full gap-3">
									<div className="flex flex-col gap-1">
										<div className="flex gap-2">
											<Button
												size="sm"
												variant="light"
												color="danger"
												onPress={() => setConfirmingRotate(true)}
												isDisabled={loading || !url}
											>
												Rotate link
											</Button>
											<Button
												size="sm"
												variant="light"
												color="danger"
												onPress={() => setConfirmingUnsubscribe(true)}
												isDisabled={loading || !url}
											>
												Turn off feed
											</Button>
										</div>
										<p className="text-[11px] text-default-500 max-w-md">
											Both stop every device already set up from updating. <strong>Rotate</strong>{' '}
											replaces this link with a new one and isn't needed just to add another device
											— only use it if this link may have leaked. <strong>Turn off</strong> removes
											your link entirely; subscribing again later gives you a different one.
										</p>
									</div>
									<Button size="sm" variant="light" onPress={close}>
										Done
									</Button>
								</div>
							)}
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>
	);
}

export default SubscribeDialog;
